"use client";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { useState } from "react";
import Image from "next/image";
import { toWebp } from "@/utils/common";

interface CareerSliderProps {
  data?: any;
}

const baseUrl = `${process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL}`;

const getImageUrl = (url?: string) => {
  if (!url) return "";
  return url.startsWith("http")
    ? url
    : `${baseUrl.replace(/\/$/, "")}${url}`;
};


const TestimonialSlider: React.FC<CareerSliderProps> = ({ data }) => {
  const [currentIndex, setCurrentIndex] = useState(1);

  return (
    <div className="relative w-full">
      <div>
        <div>
          {data?.length > 0 ? (
            <Swiper
              modules={[EffectFade, Navigation, Autoplay]}
              effect="fade"
              navigation={{
                prevEl: ".prevBtn",
                nextEl: ".nextBtn",
              }}
              autoplay={{ delay: 5000, disableOnInteraction: false }}
              loop={true}
              onSlideChange={(swiper) => setCurrentIndex(swiper.realIndex + 1)}
              className="overflow-hidden h-full"
            >
              {data?.map((item: any, index: number) => (
                <SwiperSlide key={index} className="CareerSliderHeight">
                  <div className="grid grid-cols-1 md:grid-cols-[25%_50%_25%] xl:grid-cols-3 gap[0px] h-full">
                    <div className="md:block hidden h-full">
                      {item?.image?.data?.attributes?.url && (
                        <>
                          <Image
                            src={toWebp(getImageUrl(item?.image?.data?.attributes?.url))}
                            alt={item?.author || "slide"}
                            width={410}
                            height={410}
                            className="w-full h-full object-cover"
                          />
                        </>
                      )}
                    </div>
                    <div className="bg-white relative before:bg-[url('/images/career-blue-shape-b.png')] before:bg-center before:bg-no-repeat before:bg-cover before:content-[''] before:absolute before:bottom-0 before:left-0 before:w-full before:h-full before:z-[1]">
                      {/* Corner dots */}
                      <span className="absolute z-10 shadow-[0_3px_6px_0_#00000033] bg-white rounded-full w-[10px] h-[10px] top-[12px] left-[12px]"></span>
                      <span className="absolute z-10 shadow-[0_3px_6px_0_#00000033] bg-white rounded-full w-[10px] h-[10px] top-[12px] right-[12px]"></span>
                      <span className="absolute z-10 shadow-[0_3px_6px_0_#00000033] bg-white rounded-full w-[10px] h-[10px] bottom-[12px] left-[12px]"></span>
                      <span className="absolute z-10 shadow-[0_3px_6px_0_#00000033] bg-white rounded-full w-[10px] h-[10px] bottom-[12px] right-[12px]"></span>

                      <div className="career-content p-[48px_20px_24px_20px] md:p-[48px_24px_24px_24px] relative z-10 h-full flex flex-col justify-between">
                        <p className="text-center text-[#040405] font-[400] xl:text-[18px] xl:leading-[25.2px] text-[16px] leading-[22.4px] pb-[94px] md:pb-[24px]">
                          {item?.description}
                        </p>
                        <div className="border-[#B0D9F7] border-t pt-[16px]">
                          <h6 className="text-center w-full text-[#040405] font-[600] text-[18px] leading-[28px] xl:text-[20px] xl:leading-[28px]">
                            {item?.author}
                          </h6>
                        </div>
                      </div>
                    </div>
                    <div className="md:block hidden h-full">
                      {item?.image_2?.data?.attributes?.url && (
                        <>
                          <Image
                            src={toWebp(getImageUrl(item?.image_2?.data?.attributes?.url))}
                            alt={item?.author || "slide"}
                            width={410}
                            height={410}
                            className="w-full h-full object-cover"
                          />
                        </>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <p className="text-center text-gray-500 py-10">
              No testimonials available
            </p>
          )}
        </div>
      </div>

      {/* Desktop Controls */}
      {data?.length > 0 && (
        <div className="justify-center items-center gap-3 mt-[40px] hidden md:flex">
          <button
            aria-label="Previous"
            className="prevBtn p-2 border hover:border-[#0086E6] border-border opacity-70 hover:opacity-100 cursor-pointer transition-all duration-300 bg-white"
          >
            <svg
              className="arrow arrow--left"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M9.57 5.93L3.5 12l6.07 6.07"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="stroke-text-button"
              />
              <path
                d="M20.5 12H3.67"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="stroke-text-button"
              />
            </svg>
          </button>

          <div className="pagination-indicator text-sm font-medium">
            <span className="text-subtitle-1 font-bold text-secondary-text">
              {currentIndex}/{data?.length}
            </span>
          </div>

          <button
            aria-label="Next"
            className="nextBtn hover:border-[#0086E6] p-2 border border-border opacity-70 hover:opacity-100 cursor-pointer transition-all duration-300 bg-white"
          >
            <svg
              className="arrow arrow--right"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M14.43 5.93L20.5 12l-6.07 6.07"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="stroke-text-button"
              />
              <path
                d="M3.5 12h16.83"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="stroke-text-button"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Mobile Progress Bar */}
      {data?.length > 0 && (
        <div className="mt-[30px] lg:mt-[15px] md:hidden flex justify-center items-center gap-2 px-12 py-6 sm:w-[67%] mx-auto relative ">
          {data?.map((_: any, idx: number) => (
            <div
              key={idx}
              className={`p-0 relative rounded overflow-hidden ${currentIndex - 1 === idx
                ? "flex w-[128px] h-1 bg-background"
                : "flex-none w-[16px] h-1 bg-background"
                }`}
            >
              <span
                className={`absolute top-0 left-0 rounded h-1 ${currentIndex - 1 === idx
                  ? "bg-gradient-to-r from-[#8ABC00] to-[#0086E6] transition-all duration-[5000ms] w-[128px]"
                  : "w-[16px]"
                  }`}
              ></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
export default TestimonialSlider;